import React, { useEffect, useState } from 'react';
import { SpaceWrapper } from 'ui-toolkit';
import GrayLine from '../../../shared/GrayLine';
import { initCdItems, loadItemsData } from '../../../../store/order/cdBuilder';
import { useAppDispatch } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { RootStore } from '../../../../store/types';
import { useKeyToSelect } from '../../../../store/createKeyedDataAsyncSlice';
import { getEscrowStatementAction } from '../../../../store/order/escrow/escrowStatementSlice';
import { byCDItemsForEscrowTable } from '../../../../utilities/order/escrow/filterCDItemsForEscrowTable';
import { ePayee, IDueTo } from '../../../../types/order/escrow/IDueTo';
import { isUnison } from '../../../../utilities/isUnison';
import { ISection } from '../../../../types/order/escrow/ISettlementStatement';
import { useFormikContext } from 'formik';
import { SpaCDItemTable } from './spa/SpaCDItemTable';
import { IFetchStatus } from '../../../../types/rest/IFetchStatus';
import { ThunkDispatch } from 'redux-thunk';
import { UUID } from '../../../../types/shared/UUID';
import { AnyAction, CombinedState } from '@reduxjs/toolkit';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { useGetDetailedEscrowDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { ICDV3ViewItem } from '../../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { eTransactionCategory } from '../../../../ui-toolkit/enums/eTransactionType';

interface ICDItemControlProps {}

const getDueTo = (
  lenderName: string | undefined,
  escrowStatementSections: ISection[],
  transactionCategory: eTransactionCategory
): { dueToBuyer?: IDueTo; dueToSeller?: IDueTo } => {
  if (transactionCategory === eTransactionCategory.Purchase) {
    const sectionDueToBuyer = escrowStatementSections.filter(
      (t) => t.sectionType === 'balance_buyer' && t.sectionHeader.label === 'BALANCE DUE TO BUYER'
    );
    const amountDueToBuyer = sectionDueToBuyer?.length ? sectionDueToBuyer[0].sectionHeader.debit2 || 0 : 0;

    const sectionDueToSeller = escrowStatementSections.filter(
      (t) => t.sectionType === 'balance_seller' && t.sectionHeader.label === 'BALANCE DUE TO SELLER'
    );
    const amountDueToSeller = sectionDueToSeller.length ? sectionDueToSeller[0].sectionHeader.debit1 || 0 : 0;
    return {
      dueToSeller: { isHidden: false, payee: ePayee.DueToSeller, amount: amountDueToSeller },
      dueToBuyer: { isHidden: false, payee: ePayee.DueToBuyer, amount: amountDueToBuyer },
    };
  } else {
    if (isUnison(lenderName)) {
      const sectionDueToBuyer = escrowStatementSections.filter(
        (t) => t.sectionType === 'subtotals' && t.sectionHeader.label === 'NET PROCEEDS TO CUSTOMER'
      );
      const amountDueToBuyer = sectionDueToBuyer?.length ? sectionDueToBuyer[0].sectionHeader.debit1 || 0 : 0;

      const sectionDueToSeller = escrowStatementSections.filter(
        (t) => t.sectionType === 'totals' && t.sectionHeader.label === 'NET FEES TO UNISON'
      );
      const amountDueToSeller = sectionDueToSeller?.length ? sectionDueToSeller[0].sectionHeader.debit1 || 0 : 0;
      return {
        dueToSeller: { isHidden: false, payee: ePayee.DueToUnison, amount: amountDueToSeller },
        dueToBuyer: { isHidden: false, payee: ePayee.DueToCustomer, amount: amountDueToBuyer },
      };
    } else {
      const sectionDueToBorrower = escrowStatementSections.filter(
        (t) => t.sectionType === 'balance_borrower' && t.sectionHeader.label === 'BALANCE DUE TO BORROWER'
      );
      const amountDueToBorrower = sectionDueToBorrower?.length ? sectionDueToBorrower[0].sectionHeader.debit1 || 0 : 0;
      return { dueToBuyer: { isHidden: false, payee: ePayee.DueToBorrower, amount: amountDueToBorrower } };
    }
  }
};

const fetchCDItems = (
  dispatch: ThunkDispatch<CombinedState<any>, null, AnyAction>,
  orderId: UUID,
  transactionCategory?: eTransactionCategory
) => {
  // get cd items logic is legacy.
  dispatch(initCdItems(orderId, []));
  if (transactionCategory) {
    dispatch(
      loadItemsData(orderId, [], {
        ownerType: 'order',
        transactionType: transactionCategory,
        loanAmount: 0,
        addressInfo: { state: '', street_address: '', city: '', zip_code: '' },
      })
    );
  }
  dispatch(getEscrowStatementAction(orderId));
};

export const SpaEscrowCDItemsContainer: React.FC<ICDItemControlProps> = () => {
  const { data } = useSelectCurrentOrder();
  const orderId = data.transaction.id;
  assertDefined(orderId);
  const transactionCategory = data.transaction.transaction_category;
  const lenderName = data.lender.name || undefined;

  const dispatch = useAppDispatch();
  const cdItemsData = useSelector((rootStore: RootStore) => rootStore.order.cdBuilder.cdItems);

  const { data: remittance } = useKeyToSelect(orderId, (store: RootStore) => store.order.escrow.remittanceSlice);
  const { data: escrowStatement } = useKeyToSelect(orderId, (store: RootStore) => store.order.escrow.escrowStatement);
  const { data: escrowFileCompleteData } = useGetDetailedEscrowDataQuery(orderId);

  const {
    deleteAchPush: { fetchStatus: delAchPushStatus },
    deleteInternalTransfer: { fetchStatus: delTransferStatus },
    deleteManualCheck: { fetchStatus: delCheckStatus },
    deleteWireDisbursement: { fetchStatus: delWireStatus },
  } = useSelector((rootStore: RootStore) => rootStore.order.escrow.disbursements);

  const [dueToBuyer, setDueToBuyer] = useState<IDueTo | undefined>(undefined);
  const [dueToSeller, setDueToSeller] = useState<IDueTo | undefined>(undefined);

  const formik = useFormikContext();
  const onAddTransaction = (fieldName: string, amount: number, cdItemId: string) => {
    formik.setFieldTouched(fieldName as never);
    formik.setFieldValue(fieldName, [
      ...(formik.values as any)[fieldName],
      {
        amountCents: amount,
        cdItemId: cdItemId,
      },
    ]);
  };

  useEffect(() => {
    // get cd items logic is legacy.
    fetchCDItems(dispatch, orderId, transactionCategory);
  }, [dispatch, orderId, transactionCategory]);

  useEffect(() => {
    if (
      [delAchPushStatus, delTransferStatus, delWireStatus, delCheckStatus].some(({ success }: IFetchStatus) => success)
    ) {
      fetchCDItems(dispatch, orderId, transactionCategory);
    }
  }, [dispatch, orderId, transactionCategory, delAchPushStatus, delTransferStatus, delWireStatus, delCheckStatus]);

  const cdItems: ICDV3ViewItem[] = (cdItemsData[orderId]?.items.asArray || []).filter(
    (t) => !(remittance || []).find((remit) => remit.cdItemsV3Id === t.id)
  );

  // from reducer
  const filteredCDItemList = cdItems
    .filter(byCDItemsForEscrowTable)
    .filter(
      (item) => !(escrowFileCompleteData?.cdItemsResponse || []).find((remitItem) => item.id === remitItem.itemId)
    );

  useEffect(() => {
    if (escrowStatement?.statement.sections && filteredCDItemList && !dueToBuyer && transactionCategory) {
      const dueToValues = getDueTo(lenderName, escrowStatement?.statement.sections || [], transactionCategory);
      dueToValues.dueToBuyer && setDueToBuyer(dueToValues.dueToBuyer);
      dueToValues.dueToSeller && setDueToSeller(dueToValues.dueToSeller);
    }
  }, [filteredCDItemList, transactionCategory, lenderName, escrowStatement?.statement.sections, dueToBuyer]);

  return (
    <>
      {cdItems.length > 0 && (
        <>
          <SpaCDItemTable
            cdItemList={filteredCDItemList}
            dueToBuyer={dueToBuyer || { payee: ePayee.DueToBuyer, amount: 0, isHidden: false }}
            dueToSeller={dueToSeller || { payee: ePayee.DueToBuyer, amount: 0, isHidden: false }}
            hideDueToBorrower={() => dueToBuyer && setDueToBuyer({ ...dueToBuyer, isHidden: true })}
            hideDueToSeller={() => dueToSeller && setDueToSeller({ ...dueToSeller, isHidden: true })}
            onAddTransaction={onAddTransaction}
          />
          <GrayLine />
          <SpaceWrapper topSpace={'roomy'} bottomSpace={'roomy'} leftSpace={'none'} rightSpace={'none'} />
        </>
      )}
    </>
  );
};
